import {
  ASSIGN_COVERAGE_SUCCESS,
  CANCEL_COVERAGE_SUCCESS,
  DISMISS_NOTIFICATION_SUCCESS,
  UPDATE_COVERAGE_SUCCESS,
} from './myFiles.types';
import { Add as AddIcon } from '@material-ui/icons';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
} from '@material-ui/core';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { makeStyles } from '@material-ui/core/styles';
import ActiveFilesTable from './components/ActiveFilesTable.component';
import ClosedFilesTable from './components/ClosedFilesTable.component';
import CoverageAssignmentModal from './components/CoverageAssignmentModal.component';
import CoverageChip from './components/CoverageChip.component';
import CoveringFilesTable from './components/CoveringFilesTable.component';
import NotificationBanner from 'modules/common/components/NotificationBanner.component';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 'bold',
  },
}));

const MyFilesComponent = ({
  selectedUserId,
  userName,
  activeFiles,
  coveringFiles,
  closedFiles,
  closedFilesTotalCount,
  currentCoverage,
  internalUsers,
  notifications,
  isLoadingActiveFiles,
  isLoadingCoveringFiles,
  isLoadingClosedFiles,
  isLoadingCoverage,
  isLoadingUsers,
  isCoverageModalOpen,
  isEditingCoverage,
  existingCoverageData,
  allEnums,
  getActiveFiles,
  getCoveringFiles,
  getClosedFiles,
  getCurrentCoverage,
  assignCoverage,
  updateCoverage,
  cancelCoverage,
  getInternalUsers,
  getNotifications,
  dismissNotification,
  setCoverageModalOpen,
  setCoverageModalClosed,
  showToast,
  isRCRole,
}) => {
  const classes = useStyles();
  const [closedFilesPage, setClosedFilesPage] = useState(0);
  const [closedFilesRowsPerPage, setClosedFilesRowsPerPage] = useState(10);

  // Handle closed files pagination
  const handleClosedFilesPageChange = (event, newPage) => {
    setClosedFilesPage(newPage);
    getClosedFiles(selectedUserId, newPage + 1, closedFilesRowsPerPage);
  };

  const handleClosedFilesRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setClosedFilesRowsPerPage(newRowsPerPage);
    setClosedFilesPage(0);
    getClosedFiles(selectedUserId, 1, newRowsPerPage);
  };

  // Handle coverage modal
  const handleOpenCoverageModal = () => {
    if (!isLoadingUsers && internalUsers.length === 0) {
      getInternalUsers();
    }
    setCoverageModalOpen(true, null);
  };

  const handleEditCoverage = (coverage) => {
    if (!isLoadingUsers && internalUsers.length === 0) {
      getInternalUsers();
    }
    setCoverageModalOpen(true, coverage);
  };

  const handleCloseCoverageModal = () => {
    setCoverageModalClosed();
  };

  const handleCoverageSubmit = async (coverageData) => {
    const payload = {
      ...coverageData,
      createdBy: selectedUserId,
    };

    let action;
    if (isEditingCoverage) {
      action = await updateCoverage(payload);
    } else {
      action = await assignCoverage(payload);
    }

    if (action.type === ASSIGN_COVERAGE_SUCCESS || action.type === UPDATE_COVERAGE_SUCCESS) {
      showToast(
        isEditingCoverage ? 'Coverage updated successfully' : 'Coverage assigned successfully',
        { severity: TOAST_MESSAGE_SEVERITY_SUCCESS },
      );
      // Reload data
      getCurrentCoverage(selectedUserId);
      getActiveFiles(selectedUserId);
    } else {
      showToast(
        action.messages?.[0] || 'Failed to save coverage',
        { severity: TOAST_MESSAGE_SEVERITY_ERROR },
      );
    }
  };

  const handleCancelCoverage = async () => {
    if (window.confirm('Are you sure you want to cancel all coverage assignments?')) {
      const action = await cancelCoverage(selectedUserId);

      if (action.type === CANCEL_COVERAGE_SUCCESS) {
        showToast('Coverage cancelled successfully', {
          severity: TOAST_MESSAGE_SEVERITY_SUCCESS,
        });
        getCurrentCoverage(selectedUserId);
        getActiveFiles(selectedUserId);
      } else {
        showToast(
          action.messages?.[0] || 'Failed to cancel coverage',
          { severity: TOAST_MESSAGE_SEVERITY_ERROR },
        );
      }
    }
  };

  const handleDismissNotification = async (notificationId) => {
    const action = await dismissNotification(notificationId);

    if (action.type === DISMISS_NOTIFICATION_SUCCESS) {
      // Notification is automatically removed from state by reducer
    } else {
      showToast(
        action.messages?.[0] || 'Failed to dismiss notification',
        { severity: TOAST_MESSAGE_SEVERITY_ERROR },
      );
    }
  };

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Typography variant="h4" gutterBottom>
        My Files
      </Typography>

      {/* Notifications */}
      {notifications && notifications.length > 0 && (
        <Box mb={2}>
          {notifications.map((notification) => (
            <NotificationBanner
              key={notification.userNotificationId}
              notification={notification}
              onDismiss={handleDismissNotification}
            />
          ))}
        </Box>
      )}

      {/* Active Files Section */}
      <Paper className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography variant="h6" className={classes.title}>
            Active Files ({activeFiles.length})
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCoverageModal}
            disabled={activeFiles.length === 0}
          >
            Assign Coverage
          </Button>
        </div>

        {/* Current Coverage Chip */}
        {currentCoverage && currentCoverage.length > 0 && (
          <CoverageChip
            coverage={currentCoverage}
            onEdit={handleEditCoverage}
            onCancel={handleCancelCoverage}
          />
        )}

        <ActiveFilesTable
          files={activeFiles}
          isLoading={isLoadingActiveFiles}
        />
      </Paper>

      {/* Covering Files Section */}
      <Paper className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography variant="h6" className={classes.title}>
            Covering For Others ({coveringFiles.length})
          </Typography>
        </div>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Files you are covering while others are away
        </Typography>
        <CoveringFilesTable
          files={coveringFiles}
          isLoading={isLoadingCoveringFiles}
        />
      </Paper>

      {/* Closed Files Section */}
      <Paper className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography variant="h6" className={classes.title}>
            Closed Files
          </Typography>
        </div>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Your completed and cancelled files
        </Typography>
        <ClosedFilesTable
          files={closedFiles}
          totalCount={closedFilesTotalCount}
          isLoading={isLoadingClosedFiles}
          page={closedFilesPage}
          rowsPerPage={closedFilesRowsPerPage}
          onPageChange={handleClosedFilesPageChange}
          onRowsPerPageChange={handleClosedFilesRowsPerPageChange}
        />
      </Paper>

      {/* Coverage Assignment Modal */}
      <CoverageAssignmentModal
        open={isCoverageModalOpen}
        onClose={handleCloseCoverageModal}
        onSubmit={handleCoverageSubmit}
        files={activeFiles}
        internalUsers={internalUsers}
        existingCoverage={existingCoverageData}
        isEditing={isEditingCoverage}
        isLoading={isLoadingCoverage}
        currentUserName={userName}
        selectedUserId={selectedUserId}
        isRCRole={isRCRole}
      />
    </Container>
  );
};

MyFilesComponent.propTypes = {
  selectedUserId: PropTypes.number,
  userName: PropTypes.string,
  activeFiles: PropTypes.array.isRequired,
  coveringFiles: PropTypes.array.isRequired,
  closedFiles: PropTypes.array.isRequired,
  closedFilesTotalCount: PropTypes.number.isRequired,
  currentCoverage: PropTypes.array,
  internalUsers: PropTypes.array.isRequired,
  notifications: PropTypes.array.isRequired,
  isLoadingActiveFiles: PropTypes.bool.isRequired,
  isLoadingCoveringFiles: PropTypes.bool.isRequired,
  isLoadingClosedFiles: PropTypes.bool.isRequired,
  isLoadingCoverage: PropTypes.bool.isRequired,
  isLoadingUsers: PropTypes.bool.isRequired,
  isCoverageModalOpen: PropTypes.bool.isRequired,
  isEditingCoverage: PropTypes.bool.isRequired,
  existingCoverageData: PropTypes.array,
  allEnums: PropTypes.object,
  getActiveFiles: PropTypes.func.isRequired,
  getCoveringFiles: PropTypes.func.isRequired,
  getClosedFiles: PropTypes.func.isRequired,
  getCurrentCoverage: PropTypes.func.isRequired,
  assignCoverage: PropTypes.func.isRequired,
  updateCoverage: PropTypes.func.isRequired,
  cancelCoverage: PropTypes.func.isRequired,
  getInternalUsers: PropTypes.func.isRequired,
  getNotifications: PropTypes.func.isRequired,
  dismissNotification: PropTypes.func.isRequired,
  setCoverageModalOpen: PropTypes.func.isRequired,
  setCoverageModalClosed: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  // False when the dashboard belongs to an expense analyst rather than a relo counselor
  isRCRole: PropTypes.bool,
};

MyFilesComponent.defaultProps = {
  isRCRole: true,
};

export default MyFilesComponent;
